import { Component, type OnInit, type OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MarkdownComponent } from 'ngx-markdown';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faInfoCircle,
  faExclamationTriangle,
  faExclamationCircle,
  type IconDefinition
} from '@fortawesome/free-solid-svg-icons';
import { type INotificationData, NotificationSeverity } from '@odin/odin-core';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { NotificationDataService } from '@odin/odin-core';

@Component({
  standalone: true,
  selector: 'odin-notifications-page',
  templateUrl: './notifications-page.component.html',
  styleUrls: ['./notifications-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FontAwesomeModule,
    MarkdownComponent,
  ],
})
export class NotificationsPageComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  readonly notifications$: Observable<INotificationData[]> =
    this.notificationDataService.getNotifications().pipe(
      tap(notifications => {
        // Mark unread notifications as read whenever the list updates
        notifications
          .filter(n => !n.read)
          .forEach(n => this.notificationDataService.markAsRead(n.messageId));
      }),
      takeUntil(this.destroy$)
    );

  constructor(
    private readonly notificationDataService: NotificationDataService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getIcon(severity: NotificationSeverity): IconDefinition {
    switch (severity) {
      case NotificationSeverity.Info:
        return faInfoCircle;
      case NotificationSeverity.Warning:
        return faExclamationTriangle;
      case NotificationSeverity.Critical:
        return faExclamationCircle;
      default:
        return faInfoCircle;
    }
  }
}